/**
 * Navigation admin regroupée — BT-025 + BT-GSD-KARATE-ONBOARDING-NAV-001.
 * Principal (Accueil / Tableau de bord / Formation) puis sections métier.
 */
import { resolveKarateNavItems } from "@/config/karateNavConfig.js";
import { getLocaleLabel } from "@/coquille-bridge";
import { ADMIN_SHOTOKAN_BASE } from "@/pages/shotokanBlackDragon/shotokanNavConfig.js";
import {
  ADMIN_DOCUMENTS_PREUVES_BASE,
  DOCUMENTS_PREUVES_MODULE_EN,
  DOCUMENTS_PREUVES_MODULE_FR,
  SHOTOKAN_DOCUMENTS_PREUVES_BASE,
} from "@/pages/documentsPreuves/documentsPreuvesNavConfig.js";

/** @typedef {{ id: string, label: string, labelFr: string, labelEn: string, collapsible?: boolean, hubPath?: string, icon?: string, items: object[] }} AdminNavGroup */

const ORGANISATION_PATHS = [
  "/admin/organisation",
  "/admin/dojos",
  "/hanshi/instructeurs",
  "/hanshi/membres",
  "/admin/etudiants",
  "/admin/membres-officiels",
  "/admin/assistants",
  "/admin/parents",
];

const ACTIVITES_PATHS = ["/admin/classes", "/admin/evenements", "/admin/presences", "/admin/communications", "/admin/documents"];

const PROGRESSION_PATHS = ["/admin/ceintures", "/admin/progression", "/admin/examens"];

const ADMINISTRATION_PATHS = [
  "/admin/comptes",
  "/admin/preparation-liste-officielle",
  "/admin/roles",
  "/admin/demandes",
  "/admin/adhesions",
  "/admin/black-dragon-adhesions",
  "/admin/import",
  "/admin/competitions",
  "/admin/paiements",
  "/admin/rapports",
  "/admin/presentation-interactive",
];

const AIDE_PATHS = ["/aide"];

const SHOTOKAN_SIDEBAR_ITEMS = [
  {
    id: "sbd-overview",
    to: ADMIN_SHOTOKAN_BASE,
    end: true,
    labelFr: "Vue d'ensemble",
    labelEn: "Overview",
    icon: "🏠",
  },
  {
    id: "sbd-titres",
    to: `${ADMIN_SHOTOKAN_BASE}/titres`,
    labelFr: "Titres",
    labelEn: "Titles",
    icon: "🏅",
  },
  {
    id: "sbd-demandes",
    to: `${ADMIN_SHOTOKAN_BASE}/demandes`,
    labelFr: "Demandes de titres",
    labelEn: "Title requests",
    icon: "📋",
  },
  {
    id: "sbd-reconnaissances",
    to: `${ADMIN_SHOTOKAN_BASE}/reconnaissances`,
    labelFr: "Reconnaissances",
    labelEn: "Recognitions",
    icon: "✅",
  },
  {
    id: "sbd-autorite",
    to: `${ADMIN_SHOTOKAN_BASE}/autorite`,
    labelFr: "Autorité",
    labelEn: "Authority",
    icon: "⚖️",
  },
  {
    id: "sbd-bd-adhesions",
    to: "/admin/black-dragon-adhesions",
    labelFr: "Adhésions Black Dragon",
    labelEn: "Black Dragon memberships",
    icon: "🐉",
  },
  {
    id: "sbd-documents-preuves",
    to: SHOTOKAN_DOCUMENTS_PREUVES_BASE,
    labelFr: DOCUMENTS_PREUVES_MODULE_FR,
    labelEn: DOCUMENTS_PREUVES_MODULE_EN,
    icon: "📄",
    matchPrefixes: [SHOTOKAN_DOCUMENTS_PREUVES_BASE, ADMIN_DOCUMENTS_PREUVES_BASE],
  },
];

const GROUP_META = [
  {
    id: "principal",
    labelFr: "Principal",
    labelEn: "Main",
    icon: "🏠",
    collapsible: false,
    paths: ["/accueil", "/admin", "/formation"],
  },
  {
    id: "organisation",
    labelFr: "Organisation",
    labelEn: "Organization",
    icon: "🏢",
    hubPath: "/admin/organisation-hub",
    paths: ORGANISATION_PATHS,
  },
  {
    id: "activites",
    labelFr: "Activités dojo",
    labelEn: "Dojo activities",
    icon: "📅",
    hubPath: "/admin/activites-hub",
    paths: ACTIVITES_PATHS,
  },
  {
    id: "progression",
    labelFr: "Progression martiale",
    labelEn: "Martial progression",
    icon: "🥋",
    hubPath: "/admin/progression-hub",
    paths: PROGRESSION_PATHS,
  },
  {
    id: "shotokan",
    labelFr: "Shotokan Black Dragon",
    labelEn: "Shotokan Black Dragon",
    icon: "🐉",
    customItems: SHOTOKAN_SIDEBAR_ITEMS,
    matchPrefixes: [ADMIN_SHOTOKAN_BASE, ADMIN_DOCUMENTS_PREUVES_BASE],
  },
  {
    id: "administration",
    labelFr: "Administration",
    labelEn: "Administration",
    icon: "⚙️",
    hubPath: "/admin/administration-hub",
    paths: ADMINISTRATION_PATHS,
  },
  {
    id: "aide",
    labelFr: "Aide",
    labelEn: "Help",
    icon: "❓",
    collapsible: false,
    paths: AIDE_PATHS,
  },
];

function localizeItem(item, locale) {
  return {
    ...item,
    label: getLocaleLabel(item, locale),
  };
}

function pickItems(flatItems, paths, locale) {
  const byPath = new Map(flatItems.map((i) => [i.to, i]));
  return paths
    .map((p) => byPath.get(p))
    .filter(Boolean)
    .map((i) => localizeItem(i, locale));
}

/**
 * @param {'fr'|'en'} locale
 * @returns {AdminNavGroup[]}
 */
export function getAdminNavGroups(locale = "fr") {
  const flat = resolveKarateNavItems("admin", locale).filter(
    (i) => i.to !== ADMIN_DOCUMENTS_PREUVES_BASE && i.to !== ADMIN_SHOTOKAN_BASE,
  );

  return GROUP_META.map((meta) => {
    const items = meta.customItems
      ? meta.customItems.map((i) => localizeItem(i, locale))
      : pickItems(flat, meta.paths || [], locale);

    return {
      id: meta.id,
      label: getLocaleLabel(meta, locale),
      labelFr: meta.labelFr,
      labelEn: meta.labelEn,
      icon: meta.icon,
      collapsible: meta.collapsible !== false,
      hubPath: meta.hubPath,
      matchPrefixes: meta.matchPrefixes,
      items,
    };
  }).filter((g) => g.items.length > 0);
}

/** Liste aplatie (compat tests / mobile compact). */
export function getAdminSidebarItems(locale = "fr") {
  return getAdminNavGroups(locale).flatMap((g) => g.items);
}

/** Items compacts pour nav mobile admin — Principal en entier + hubs. */
export function getAdminMobileNavItems(locale = "fr") {
  return getAdminNavGroups(locale).flatMap((g) => {
    if (!g.collapsible) return g.items;
    return [
      {
        id: `mobile-nav-${g.id}`,
        to: g.hubPath || g.items[0]?.to || "/accueil",
        label: g.label,
        icon: g.icon || "•",
      },
    ];
  });
}

/** Détermine quel groupe contient le pathname courant. */
export function findAdminNavGroupForPath(pathname) {
  for (const group of GROUP_META) {
    if (group.id === "principal") {
      if (pathname === "/accueil" || pathname === "/admin" || pathname === "/formation" || pathname.startsWith("/formation")) {
        return "principal";
      }
      continue;
    }
    if (group.id === "aide") {
      if (pathname === "/aide" || pathname.startsWith("/aide/")) return "aide";
      continue;
    }
    if (group.matchPrefixes?.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
      return group.id;
    }
    if (group.customItems) {
      const match = group.customItems.some((item) => {
        if (item.matchPrefixes) {
          return item.matchPrefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
        }
        const to = item.to;
        return item.end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);
      });
      if (match) return group.id;
    }
    if (group.paths?.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
      return group.id;
    }
  }
  return null;
}

export { GROUP_META as KARATE_ADMIN_GROUP_META };
